import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Logo } from '../ui/Logo'
import { useI18n } from '../../i18n'
import { useAuth } from '../../hooks/use-auth'
import { usePlan } from '../../hooks/use-plan'
import { PricingHero } from './PricingHero'
import { PricingTierCards } from './PricingTierCards'
import { PricingComparisonTable } from './PricingComparisonTable'
import { PricingFAQ } from './PricingFAQ'
import { PricingContactCTA } from './PricingContactCTA'
import { PaymentDialog } from './PaymentDialog'
import type { Plan } from '../../types'

export default function PricingPage() {
  const { t } = useI18n()
  const { user, isLoggedIn } = useAuth()
  const { plans, currentPlan, fetchPlans, fetchCurrentPlan, loading } = usePlan()
  const navigate = useNavigate()

  const [paymentOpen, setPaymentOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)

  useEffect(() => {
    fetchPlans().catch(() => {
      navigate('/login')
    })
    if (isLoggedIn) {
      fetchCurrentPlan()
    }
  }, [isLoggedIn])

  const handleSelectPlan = (plan: Plan) => {
    if (plan.planCode === 'FREE') {
      navigate('/login?mode=register')
      return
    }
    if (!isLoggedIn) {
      navigate('/login?redirect=/pricing')
      return
    }
    setSelectedPlan(plan)
    setPaymentOpen(true)
  }

  const handlePaymentSuccess = () => {
    fetchCurrentPlan()
    fetchPlans()
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* 顶栏 */}
      <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-2 shrink-0">
          <Logo size={24} />
          <span className="text-[#1a1a1a]">PrimeBook</span>
          <span className="text-orange-500">AI</span>
        </Link>
        {isLoggedIn && (
          <Link to="/workspace" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all mr-4">
            <ArrowLeft className="h-3.5 w-3.5 inline mr-1.5" />
            {t('返回工作台', 'Back')}
          </Link>
        )}
        <nav className="hidden md:flex items-center gap-1">
          <Link to="/" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            {t('首页', 'Home')}
          </Link>
          <button className="px-3 py-1.5 text-[13px] rounded-lg text-[#1a1a1a] font-medium bg-black/[0.04]">
            {t('定价', 'Pricing')}
          </button>
          <Link to="/about" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            {t('关于', 'About')}
          </Link>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          {!isLoggedIn && (
            <Link to="/login">
              <button className="px-4 py-1.5 text-xs font-medium rounded-xl bg-[#1a1a1a] text-white hover:bg-[#333] transition-colors">
                {t('登录', 'Login')}
              </button>
            </Link>
          )}
        </div>
      </header>

      {/* 页面内容 */}
      <main className="max-w-5xl mx-auto px-6">
        <PricingHero />
        <PricingTierCards
          plans={plans}
          currentPlanCode={currentPlan?.planCode}
          onSelectPlan={handleSelectPlan}
          loading={loading}
        />
        <PricingComparisonTable plans={plans} />
        <PricingFAQ />
        <PricingContactCTA />
      </main>

      {/* 支付弹窗 */}
      {selectedPlan && (
        <PaymentDialog
          open={paymentOpen}
          onOpenChange={setPaymentOpen}
          plan={selectedPlan}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  )
}
